import { MenuOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { Popconfirm, Select, Space, Tooltip } from 'antd';
import { arrayMoveImmutable } from 'array-move';
import { AddIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxTable, TableColumnsType } from 'components/RevxTable';
import { FilterOperatorEnum } from 'domains/appDomain';
import { MultiTypeEnum, SKU, SKUPart } from 'domains/skuDomain';
import { cloneDeep, sortBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  SortEnd,
} from 'react-sortable-hoc';
import skuService from 'services/skuService';
import './PartsTable.less';

const DragHandle = SortableHandle(() => (
  <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
));
const SortableItem = SortableElement((props: object) => <tr {...props} />);
const SortableWrapper = SortableContainer((props: object) => (
  <tbody {...props} />
));

type Props = {
  sku: Partial<SKU>;
  parts: SKUPart[];
  onConfig: (skuPart: SKUPart) => void;
};
export const PartsTable = ({ parts, sku, onConfig }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState<SKUPart[]>(parts);
  const [partSkusForAdding, setPartSkusForAdding] = useState<SKU[]>([]);
  const [loadingPartOptions, setLoadingPartOptions] = useState(false);
  const [totalPartsSkus, setTotalPartsSkus] = useState<SKU[]>([]);
  const [skuToAdd, setSkuToAdd] = useState<number | null>(null);

  const currentPartSkuIds = useMemo(() => {
    return sortBy(dataSource.map((item) => item.part.id)).join(',');
  }, [dataSource]);

  const skuOrientation = useMemo(() => {
    return cloneDeep(sku?.orientation || []);
  }, [sku.orientation]);

  useMount(async () => {
    setLoadingPartOptions(true);
    const partSkusContent = await skuService.getPagedSkus({
      page: {
        currentPage: 1,
        pageSize: 10000,
      },
      query: {
        draft: {
          operator: FilterOperatorEnum.EQUALS,
          values: [true],
        },
        multiType: {
          operator: FilterOperatorEnum.IN,
          values: [MultiTypeEnum.BOTH, MultiTypeEnum.PART],
        },
      },
    });
    setTotalPartsSkus(
      partSkusContent.content.filter((item) => !item.baseRefId)
    );
    setLoadingPartOptions(false);
  });

  const checkPartSku = useCallback(
    (partSku: SKU) => {
      if (partSku.orientation?.length > skuOrientation.length) {
        return true;
      } else if (
        (partSku?.orientation || []).sort().join('') ===
        skuOrientation.sort().join('')
      ) {
        return true;
      } else {
        return false;
      }
    },
    [skuOrientation]
  );

  useEffect(() => {
    const excludeIds: number[] = currentPartSkuIds
      .split(',')
      .map((i) => Number(i));

    setPartSkusForAdding(
      totalPartsSkus.filter(
        (sku) => !excludeIds.includes(sku.id) && checkPartSku(sku)
      )
    );
  }, [currentPartSkuIds, totalPartsSkus, checkPartSku]);

  useEffect(() => {
    setDataSource(sortBy(parts, 'sequence'));
  }, [parts]);

  const onSortEnd = async ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(dataSource, oldIndex, newIndex).filter(
        (el) => !!el
      );
      setDataSource(newData);
      const newItem = await skuService.updateSkuPartSequence(
        sku,
        newData.map((part, index) => {
          return {
            partId: part.id,
            sequence: index + 1,
          };
        }),
        sku.version || 0
      );
      dispatch.sku.setState({ curItem: newItem });
    }
  };

  const DraggableContainer = ({ ...props }: object) => (
    <SortableWrapper
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      onSortMove={() => {}}
      {...props}
    />
  );

  const DraggableBodyRow = (props: { 'data-row-key': number }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource.findIndex((x) => x.id === props['data-row-key']);
    return <SortableItem index={index} {...props} />;
  };

  const columns: TableColumnsType<SKUPart> = [
    ...(sku.readonly
      ? []
      : [
          {
            title: '',
            dataIndex: 'sort',
            width: 30,
            className: 'drag-visible',
            render: () => <DragHandle />,
          },
        ]),
    {
      title: t('platform.sku.partsTable.column.sku'),
      dataIndex: 'name',
      render(name, record) {
        return (
          <RevxButton
            type="link"
            size="small"
            onClick={(e) => {
              onConfig({
                ...record,
                part: {
                  ...record.part,
                  readonly: sku.readonly,
                },
                readonly: sku.readonly,
              });
            }}
          >
            {record.part.name}
          </RevxButton>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center',
      width: 180,
      className: 'action',
      render: (value, record) => (
        <Space>
          <Popconfirm
            placement="topRight"
            title={t('common.confirmQuestion')}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={async () => {
              const toRemovePart = dataSource.find((p) => p.id === record.id);

              if (toRemovePart) {
                const oldDs = dataSource;
                const newDs = dataSource.filter((d) => d.id !== record.id);
                setDataSource(newDs);
                try {
                  const newSkuItem = await skuService.removePart(
                    sku,
                    toRemovePart
                  );

                  newSkuItem.parts = newSkuItem.parts.filter(
                    (item) => item.id !== toRemovePart.id
                  );
                  dispatch.sku.setState({ curItem: newSkuItem });
                } catch {
                  // if any error for remove, recover the datasource
                  setDataSource(oldDs);
                }
              }
            }}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
          >
            <RevxButton disabled={sku.readonly} type="link" size="small">
              {t('common.remove')}
            </RevxButton>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const [loading, setLoading] = useState(false);

  return (
    <>
      <RevxTable<SKUPart>
        columns={columns}
        size="small"
        indentSize={20}
        loading={loading}
        dataSource={dataSource}
        components={
          sku.readonly
            ? undefined
            : {
                body: {
                  wrapper: DraggableContainer,
                  row: DraggableBodyRow,
                },
              }
        }
        footer={() =>
          sku.readonly ? undefined : (
            <div>
              <RevxFlexLayout style={{ width: 300 }}>
                <Select<number>
                  placeholder={t('platform.sku.partsTable.addSkuPlaceholder')}
                  loading={loadingPartOptions}
                  style={{ width: '100%', marginRight: 20 }}
                  value={skuToAdd ? skuToAdd : undefined}
                  showSearch
                  filterOption={(value, option) => {
                    return String(option?.children)
                      .toLocaleLowerCase()
                      .includes(value.toLocaleLowerCase());
                  }}
                  onChange={(val) => {
                    setSkuToAdd(val);
                  }}
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                >
                  {partSkusForAdding.map((partSKU) => {
                    return (
                      <Select.Option value={partSKU.id} key={partSKU.id}>
                        {partSKU.name}
                      </Select.Option>
                    );
                  })}
                </Select>

                <Tooltip title={t('platform.sku.partsTable.button.addSku')}>
                  <RevxButton
                    size="small"
                    type="primary"
                    brandGreen
                    shape="circle"
                    icon={<AddIcon />}
                    onClick={async () => {
                      if (skuToAdd) {
                        const newSku = partSkusForAdding.find(
                          (p) => p.id === skuToAdd
                        );

                        if (newSku) {
                          setLoading(true);
                          const newSkuItem = await skuService.addPartToMulti(
                            sku,
                            newSku
                          );
                          setLoading(false);
                          setSkuToAdd(null);
                          dispatch.sku.setState({ curItem: newSkuItem });
                        }
                      }
                    }}
                  ></RevxButton>
                </Tooltip>
              </RevxFlexLayout>
              {dataSource.length === 0 && (
                <div
                  className="ant-form-item-explain-error"
                  style={{ marginTop: 10 }}
                >
                  {t('platform.sku.skuPartsRequired')}
                </div>
              )}
            </div>
          )
        }
      />
    </>
  );
};
